import React, { useState } from "react";
import styled from "styled-components";
import {
    Button,
    BusinessPortalHeader,
    PortalCaption,
    PortalHighlightMetricCard,
    PortalKicker,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    PortalPanelHeader,
    PortalSectionTitle,
    PortalSurface,
    PortalSurfaceStack,
} from "src/components";
import pluralize from "pluralize";
import QrCode from "src/img/new/qr.svg";
import { capitalizeString, customNames } from "src/utils/helpers";
import LicensesOverview from "./LicensesOverview";
import PencilIcon from "src/img/edit@2x.png";
import ChangeNameDialog from "./ChangeNameDialogSalesAndPromotion";
import LicenseTierIcon from "src/img/new/user-2.svg";
import LicenseTiersModal from "./LicenseTiersModal";
import UserIcon from "src/img/new/user-2.svg";

const AccessToolsGrid = styled(PortalMetricGrid)`
    align-items: stretch;

    > * {
        min-height: 8.5rem;
    }
`;

const AccessToolActionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;

    @media (max-width: 520px) {
        align-items: stretch;
        flex-direction: column;

        button {
            width: 100%;
        }
    }
`;

const PermissionToolCard = styled(PortalMetricCard)`
    align-items: flex-start;
    border-color: #B5C2E2;
`;

const PermissionToolHeader = styled(PortalMetricLabel)`
    gap: 0.45rem;
    margin-bottom: 0.5rem;
`;

const HeaderMetricActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.9rem;
`;

const MetricLabelIcon = styled.img`
    width: ${p => p.$width || "24px"};
    height: ${p => p.$height || "24px"};
    margin: ${p => p.$margin || "0 0 0 0.5rem"};
    vertical-align: middle;
    object-fit: contain;

    [data-theme="dark"] & {
        filter: brightness(0) invert(1);
        opacity: 0.92;
    }
`;

export const getSalesAndPromotionHeading = (user) => {
    return user?.customSalesAndPromotionsName || "Sales & Promotions";
};

function SalesAndPromotionHeader(props) {
    const { handleShowGetQrModal, unusedLicensesAvailable, isFreeTrialLicense, licensesOverview, user } = props;
    const [showChangeNameDialog, setShowChangeNameDialog] = useState(false);
    const [showLicenseTiersModal, setShowLicenseTiersModal] = useState(false);

    const heading = getSalesAndPromotionHeading(user);

    const handleOpenLicenseTiers = () => {
        setShowLicenseTiersModal(true);
    };

    const handleCloseLicenseTiers = () => {
        setShowLicenseTiersModal(false);
    };

    return (
        <>
            <BusinessPortalHeader
                title={heading}
                description="Offer short-term discounted trials, monitor license capacity, and make onboarding easy with QR access."
            >
                <Button
                    backgroundColor="#171A22"
                    color="#FFFFFF"
                    height="42px"
                    borderRadius="8px"
                    padding="0 1rem"
                    gap="0.5rem"
                    onClick={() => setShowChangeNameDialog(true)}
                >
                    Change Name
                    <img
                        src={PencilIcon}
                        height="13px"
                        width="13px"
                        alt=""
                    />
                </Button>
            </BusinessPortalHeader>

            <PortalSurfaceStack maxWidth="98.625rem">
                <PortalSurface padding="1rem">
                    <PortalPanelHeader withDivider>
                        <div>
                            <PortalKicker>Commercial Overview</PortalKicker>
                            <PortalSectionTitle>License performance</PortalSectionTitle>
                            <PortalCaption>Use this overview to track trial inventory and upgrade activity.</PortalCaption>
                        </div>
                    </PortalPanelHeader>
                    <LicensesOverview licensesOverview={licensesOverview} />
                </PortalSurface>
                <PortalSurface padding="1rem">
                    <PortalPanelHeader withDivider>
                        <div>
                            <PortalKicker>Access Tools</PortalKicker>
                            <PortalSectionTitle>Trial access controls</PortalSectionTitle>
                        </div>
                    </PortalPanelHeader>
                    <PortalMetricGrid columns={4} style={{ marginTop: "1rem" }}>
                        {isFreeTrialLicense &&
                            <PortalMetricCard>
                                <PortalMetricLabel>
                                    Easy Access QR Code
                                    <MetricLabelIcon src={QrCode} alt="QR Code" />
                                </PortalMetricLabel>
                                <PortalCaption margin="0">
                                    Grant {pluralize(customNames.clients.toLowerCase())} access to your branded Me Business app with your unique QR code.
                                </PortalCaption>
                                <HeaderMetricActions>
                                    <span />
                                    <Button
                                        backgroundColor="#171A22"
                                        color="#FFFFFF"
                                        height="38px"
                                        borderRadius="8px"
                                        padding="0 0.85rem"
                                        onClick={() => handleShowGetQrModal()}
                                    > Get QR Code
                                    </Button>
                                </HeaderMetricActions>
                            </PortalMetricCard>
                        }
                        <PortalMetricCard>
                            <PortalMetricLabel>
                                <MetricLabelIcon src={LicenseTierIcon} $height="22px" $width="22px" $margin="0 0.4rem 0 0" alt="" />
                                License Tiers Available
                            </PortalMetricLabel>
                            <AccessToolActionRow>
                                <PortalMetricValue>{props?.licenseTiers?.availableCount}</PortalMetricValue>
                                <Button
                                    backgroundColor="#171A22"
                                    color="#FFFFFF"
                                    height="38px"
                                    borderRadius="8px"
                                    padding="0 0.85rem"
                                    onClick={handleOpenLicenseTiers}
                                >
                                    Manage
                                </Button>
                            </AccessToolActionRow>
                        </PortalMetricCard>
                        <PermissionToolCard>
                            <PermissionToolHeader>
                                <MetricLabelIcon src={UserIcon} $height="18px" $width="18px" $margin="0" alt="" />
                                User Permissions & Roles
                            </PermissionToolHeader>
                            <HeaderMetricActions>
                                <PortalCaption margin="0">Bulk manage access</PortalCaption>              
                                <Button
                                    backgroundColor="#171A22"
                                    color="#FFFFFF"
                                    height="38px"
                                    borderRadius="8px"
                                    padding="0 0.85rem"
                                    onClick={props.openUserPermissionsModal}
                                >
                                Manage
                                </Button>
                            </HeaderMetricActions>
                        </PermissionToolCard>
                        <PortalHighlightMetricCard>
                            <PortalMetricLabel>{capitalizeString(pluralize(customNames.clients))} Licenses Available</PortalMetricLabel>
                            <PortalMetricValue>{unusedLicensesAvailable}</PortalMetricValue>
                        </PortalHighlightMetricCard>
                    </PortalMetricGrid>
                </PortalSurface>
            </PortalSurfaceStack>
            {showChangeNameDialog && (
                <ChangeNameDialog
                    hideShowNameChangeDialog={() => setShowChangeNameDialog(false)}
                    featureName={heading}
                    token={user.token}
                />
            )}
            {showLicenseTiersModal && (
                <LicenseTiersModal
                    show={showLicenseTiersModal}
                    onClose={handleCloseLicenseTiers}
                    licenseTiers={props.licenseTiers} 
                />
            )}
        </>
    );
}

export default SalesAndPromotionHeader;
